import { Box, ButtonBase } from '@/shared/ui';
import Typography from '@/shared/ui/typography/Text';

import type { Sx } from '@/shared/types/uiTypes';

import type { MarkdownHeading } from './lib/useMarkdownHeadings';

type MonthlyRecapTableOfContentsProps = {
  headings: MarkdownHeading[];
  activeId: string | null;
  onSelect: (id: string) => void;
};

type NavItemTone = 'passed' | 'active' | 'upcoming';

const getTone = (index: number, activeIndex: number): NavItemTone => {
  if (index === activeIndex) return 'active';
  if (activeIndex !== -1 && index < activeIndex) return 'passed';
  return 'upcoming';
};

const toneSx: Record<NavItemTone, Sx> = {
  passed: { color: 'gx.border' },
  active: {
    color: 'gx.text',
    backgroundColor: 'gx.tileLight',
    borderLeft: '2px solid',
    borderLeftColor: 'gx.highlight'
  },
  upcoming: { color: 'gx.subtext' }
};

const navSx: Sx = {
  display: 'flex',
  flexDirection: 'column',
  gap: '2px',
  width: '100%'
};

const labelSx: Sx = {
  fontSize: '12px',
  fontWeight: 700,
  color: 'gx.border',
  px: 2,
  pt: 3,
  pb: 1
};

const itemBaseSx: Sx = {
  display: 'block',
  width: '100%',
  textAlign: 'left',
  fontSize: '12px',
  fontWeight: 700,
  px: 2,
  py: '6px'
};

export const MonthlyRecapTableOfContents = ({
  headings,
  activeId,
  onSelect
}: MonthlyRecapTableOfContentsProps) => {
  const activeIndex = headings.findIndex(heading => heading.id === activeId);

  return (
    <Box
      aria-label='In this recap'
      component='nav'
      sx={navSx}
    >
      <Typography sx={labelSx}>IN THIS RECAP</Typography>

      {headings.map((heading, index) => (
        <ButtonBase
          key={heading.id}
          sx={{
            ...itemBaseSx,
            ...toneSx[getTone(index, activeIndex)]
          }}
          onClick={() => {
            onSelect(heading.id);
          }}
        >
          {heading.label}
        </ButtonBase>
      ))}
    </Box>
  );
};
